// Shared helpers for the Supply card and the Float & Dilution timeline. Both read
// the server supply model (realtime GET /api/symbols/:sym/supply-timeline, built
// in src/apps/realtime/src/supply/model.ts): dates are YYYY-MM-DD, series are
// change points on today's share basis, `v: null` = no count known from that day.
import { fmtSharesShort, fmtDayMon } from './format';

export const DAY_MS = 864e5;
// The model's history is loaded in batches; past this many days the card says how old it is.
export const STALE_DAYS = 5;

export const tsOf = (d: string) => Date.parse(String(d).slice(0, 10) + 'T00:00:00Z');
export const daysSince = (d: string | null | undefined) => (d ? Math.max(0, Math.round((Date.now() - tsOf(d)) / DAY_MS)) : null);
export const fS = (v: number | null | undefined) => fmtSharesShort(v);
export const fD = (d: string | null | undefined) => (d ? fmtDayMon(d) + " '" + String(d).slice(2, 4) : '—');
export const fX = (x: number) => (x >= 10 ? Math.round(x).toLocaleString() : x.toFixed(1)) + '×';

export function riskColor(r: number) { return r >= 60 ? 'var(--down)' : r >= 35 ? 'var(--warn)' : 'var(--up)'; }
export function verdictClass(v: string) {
  return v === 'TOXIC' ? 'v-toxic' : v === 'SUPPLIED' ? 'v-supplied' : v === 'CLEAN' ? 'v-clean' : 'v-watch';
}

export interface Pt { date: string; v: number | null }

/** Value in force at time t (step series), or null before the first point / inside a gap. */
export function stepAt(series: Pt[], t: number): number | null {
  let v: number | null = null;
  for (const p of series) { if (tsOf(p.date) <= t) v = p.v; else break; }
  return v;
}

/** True when day t falls inside one of the model's unverified (masked) stretches. */
export function inMask(mask: Array<{ from: string; to: string }>, t: number) {
  return mask.some((s) => t >= tsOf(s.from) && t <= tsOf(s.to));
}

/**
 * A step series as SVG path segments — a null point breaks the line. Points left of
 * `minX` restart the segment at the edge with their value; the last segment runs to `endX`.
 */
export function stepSegments(points: Pt[], X: (d: string) => number, Y: (v: number) => number, endX: number, minX: number) {
  const segs: Array<{ d: string; x0: number }> = [];
  let cur: { d: string; x0: number } | null = null;
  for (const p of points) {
    const x = X(p.date);
    if (p.v == null) {
      if (cur) { segs.push({ d: cur.d + ' H' + Math.max(minX, x), x0: cur.x0 }); cur = null; }
      continue;
    }
    const y = Y(p.v);
    if (x <= minX) { cur = { d: 'M' + minX + ' ' + y, x0: minX }; continue; }
    if (cur) cur.d += ' H' + x + ' V' + y;
    else cur = { d: 'M' + x + ' ' + y, x0: x };
  }
  if (cur) segs.push({ d: cur.d + ' H' + endX, x0: cur.x0 });
  return segs;
}
